export interface PixelCrop {
	x: number;
	y: number;
	width: number;
	height: number;
}

export interface CropOutputOptions {
	width?: number;
	height?: number;
	mimeType?: "image/jpeg" | "image/png" | "image/webp";
	quality?: number;
}

export interface ImageResizeAttempt {
	width: number;
	height: number;
	quality: number;
	mimeType?: "image/jpeg" | "image/png" | "image/webp";
}

export const MAX_INSCRIPTION_IMAGE_BYTES = 95_000;

export function getDataUrlByteSize(dataUrl: string) {
	const [, base64 = ""] = dataUrl.split(",");
	return Math.floor((base64.length * 3) / 4);
}

function assertBrowserImageSupport() {
	if (typeof Image === "undefined" || typeof document === "undefined") {
		throw new Error("Image compression requires a browser environment.");
	}
}

async function loadImage(imageSrc: string): Promise<HTMLImageElement> {
	assertBrowserImageSupport();

	const image = new Image();
	image.src = imageSrc;

	await new Promise<void>((resolve, reject) => {
		image.onload = () => resolve();
		image.onerror = () => reject(new Error("Failed to load image"));
	});

	return image;
}

const getCroppedImg = async (
	imageSrc: string,
	pixelCrop: PixelCrop,
	options: CropOutputOptions = {}
): Promise<string> => {
	const image = await loadImage(imageSrc);
	const canvas = document.createElement("canvas");
	const outputWidth = options.width ?? Math.round(pixelCrop.width);
	const outputHeight = options.height ?? Math.round(pixelCrop.height);
	canvas.width = outputWidth;
	canvas.height = outputHeight;
	const ctx = canvas.getContext("2d");

	if (!ctx) {
		throw new Error("Canvas context is not available");
	}

	ctx.drawImage(
		image,
		pixelCrop.x,
		pixelCrop.y,
		pixelCrop.width,
		pixelCrop.height,
		0,
		0,
		outputWidth,
		outputHeight
	);

	// Convert the canvas to a data URL (base64 encoded image)
	return canvas.toDataURL(
		options.mimeType ?? "image/jpeg",
		options.quality ?? 0.86
	);
};

async function resizeImageToDataUrl(
	imageSrc: string,
	options: ImageResizeAttempt
): Promise<string> {
	const image = await loadImage(imageSrc);
	const canvas = document.createElement("canvas");
	canvas.width = options.width;
	canvas.height = options.height;

	const ctx = canvas.getContext("2d");
	if (!ctx) {
		throw new Error("Canvas context is not available");
	}

	ctx.drawImage(
		image,
		0,
		0,
		image.width,
		image.height,
		0,
		0,
		canvas.width,
		canvas.height
	);

	return canvas.toDataURL(options.mimeType ?? "image/jpeg", options.quality);
}

export async function optimizeImageDataUrlToBudget({
	dataUrl,
	attempts,
	maxBytes = MAX_INSCRIPTION_IMAGE_BYTES,
}: {
	dataUrl: string;
	attempts: ImageResizeAttempt[];
	maxBytes?: number;
}): Promise<string> {
	if (getDataUrlByteSize(dataUrl) <= maxBytes) {
		return dataUrl;
	}

	let lastSize = getDataUrlByteSize(dataUrl);
	for (const attempt of attempts) {
		const resized = await resizeImageToDataUrl(dataUrl, attempt);
		lastSize = getDataUrlByteSize(resized);
		if (lastSize <= maxBytes) {
			return resized;
		}
	}

	throw new Error(
		`Image is still too large after compression (${lastSize} bytes, max ${maxBytes}). Try a simpler image.`
	);
}

export { getCroppedImg };
